import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import Alert from '../components/ui/Alert/Alert';

import { RootState } from '../ducks/store';
import { updateAuthUserProps } from '../ducks/auth';
import { useUpdateUserReportTeamsMutation } from '../services/users';
import { setActiveTeams as setTeams } from '../ducks/features/teamsSlice';
import { hasTeamManagerRole, LOCAL_STORAGE_KEYS, ToastifyNoDefaultStyling } from '../utils';

interface IUseActiveTeams {
  setActiveTeams: (teamIds: string[]) => void;
  storeTeams: (teamIds: string[]) => void;
}

export const useActiveTeams = (): IUseActiveTeams => {
  // ===========================================================================
  // Redux
  // ===========================================================================

  const dispatch = useDispatch();

  const { userId, isTeamManager } = useSelector((state: RootState) => {
    const user = state.auth.authenticatedUser;
    const isTeamManager = hasTeamManagerRole(user);

    return {
      userId: user?._id,
      isTeamManager,
    };
  });

  // ===========================================================================
  // RTK Query
  // ===========================================================================

  const [updateUserReportTeams] = useUpdateUserReportTeamsMutation();

  // ===========================================================================
  // Functions
  // ===========================================================================

  const setActiveTeams = (teamIds: string[]) => dispatch(setTeams(teamIds));

  const storeTeams = async (reportTeams: string[]) => {
    // Team Manager: Store to DB
    if (isTeamManager && userId) {
      try {
        await updateUserReportTeams({ userId, reportTeams }).unwrap();
        dispatch(updateAuthUserProps({ reportTeams }));
      } catch (e) {
        toast.error(
          <Alert icon="error" heading="Failed to save teams. Please try again." variant="danger" />,
          ToastifyNoDefaultStyling,
        );
      }

      return;
    }

    // Other Roles: Store to local storage
    if (reportTeams.length) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.activeTeams, JSON.stringify(reportTeams));
    } else {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.activeTeams);
    }
  };

  return {
    setActiveTeams,
    storeTeams,
  };
};
